import React, { useEffect, useRef, useState } from 'react';

const CapabilityPanel = ({ image, title, description, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const currentRef = panelRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div 
      ref={panelRef}
      className="relative h-[600px] overflow-hidden group"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.8s ease-out ${index * 0.2}s, transform 0.8s ease-out ${index * 0.2}s`
      }}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col justify-end p-8 md:p-12">
        <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
          {title}
        </h3>
        <p className="text-base md:text-lg text-gray-300 max-w-md leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
};

const ThreePanelCapability = () => {
  const capabilities = [
    {
      image: "https://images.unsplash.com/photo-1648913133706-57b52812afde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxlZGl0b3JpYWwlMjB3b3Jrc3BhY2V8ZW58MHx8fGJsYWNrfDE3Njg4NDM1NzJ8MA&ixlib=rb-4.1.0&q=85",
      title: "AI Systems Development",
      description: "We build and deploy production-grade machine learning systems using advanced methods in AI, statistics, and optimisation."
    },
    {
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8YmxhY2t8MTc2ODg0MzU3OHww&ixlib=rb-4.1.0&q=85",
      title: "Data & Decision Support",
      description: "We help organisations turn complex data into reliable insights that support real decisions."
    },
    {
      image: "https://images.unsplash.com/photo-1700463108327-635ce9346d55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBpbmZyYXN0cnVjdHVyZXxlbnwwfHx8YmxhY2t8MTc2ODg0MzU4NHww&ixlib=rb-4.1.0&q=85",
      title: "Training & Capability Building",
      description: "We train teams to understand, use, and manage AI responsibly in real operational environments."
    }
  ];

  return (
    <section className="bg-black py-20">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {capabilities.map((capability, index) => (
            <CapabilityPanel
              key={index}
              image={capability.image}
              title={capability.title}
              description={capability.description}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ThreePanelCapability;